import type { DateTimeValidationError } from "@mui/x-date-pickers";
import { EnhancedDateTimePicker } from "../EnhancedDatePicker";
import { Stack } from "@mui/material";
import React, { useMemo } from "react";
import type dayjs from "dayjs";

interface IFilterDateTimeRangePicker {
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
  onChangeDate: (date: dayjs.Dayjs | null, position: "startDate" | "endDate") => void;
  onValidationChange?: (isValid: boolean) => void;
}

export const FilterDateTimeRangePicker = (props: IFilterDateTimeRangePicker) => {
  const { startDate, endDate, onChangeDate, onValidationChange } = props;

  const [errorStart, setErrorStart] = React.useState<DateTimeValidationError | null>(null);
  const [errorEnd, setErrorEnd] = React.useState<DateTimeValidationError | null>(null);

  const isRangeValid = useMemo(() => {
    if (errorStart || errorEnd) return false;

    if ((startDate && !endDate) || (!startDate && endDate)) return true;

    if (startDate && endDate) {
      return startDate.isBefore(endDate) || startDate.isSame(endDate);
    }

    return true;
  }, [startDate, endDate, errorStart, errorEnd]);

  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isRangeValid);
    }
  }, [isRangeValid, onValidationChange]);

  const errorStartMessage = useMemo(() => {
    switch (errorStart) {
      case "maxDate":
        return endDate?.isValid() ? "Start cannot be after end" : "Invalid date/time";
      case "minDate":
        return "Invalid date/time";
      case "invalidDate":
        return "Invalid date/time";
      default:
        return "";
    }
  }, [errorStart, endDate]);

  const errorEndMessage = useMemo(() => {
    switch (errorEnd) {
      case "maxDate":
        return "Invalid date/time";
      case "minDate":
        return startDate?.isValid() ? "End cannot be before start" : "Invalid date/time";
      case "invalidDate":
        return "Invalid date/time";
      default:
        return "";
    }
  }, [errorEnd, startDate]);

  return (
    <Stack sx={{ pt: 1 }} spacing={2}>
      <EnhancedDateTimePicker
        label="From"
        value={startDate}
        onChange={(newValue) => onChangeDate(newValue, "startDate")}
        clearable
        maxDate={endDate ?? undefined}
        onError={(newError) => setErrorStart(newError)}
        error={errorStartMessage !== ""}
        helperText={errorStartMessage}
      />
      <EnhancedDateTimePicker
        label="To"
        value={endDate}
        onChange={(newValue) => onChangeDate(newValue, "endDate")}
        minDate={startDate ?? undefined}
        onError={(newError) => setErrorEnd(newError)}
        clearable
        error={errorEndMessage !== ""}
        helperText={errorEndMessage}
      />
    </Stack>
  );
};
